/**
 * Environment Configuration
 * Centralizes all environment variables and provides type-safe access
 */

export interface AppConfig {
  apiUrl: string;
  environment: 'development' | 'staging' | 'production';
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
}

// Extract API hostname from URL for CSP and service worker
export const getApiHostname = (apiUrl: string): string => {
  try {
    const url = new URL(apiUrl);
    return url.hostname;
  } catch {
    return '';
  }
};

// Get all API hostnames that should be allowed (for CSP)
export const getAllowedApiHostnames = (): string[] => {
  const hostnames: string[] = [];
  
  // Production API
  hostnames.push('restaurant-reservations-api-3dta.onrender.com');
  
  // Staging API
  hostnames.push('restaurant-reservations-api-staging.onrender.com');
  
  // Also allow any onrender.com subdomain for flexibility
  return hostnames;
};

const config: AppConfig = {
  apiUrl: process.env.REACT_APP_API_URL || 'https://restaurant-reservations-api-3dta.onrender.com/api/v1',
  environment: (process.env.REACT_APP_ENV as 'development' | 'staging' | 'production') || 'production',
  isDevelopment: process.env.REACT_APP_ENV === 'development',
  isStaging: process.env.REACT_APP_ENV === 'staging',
  isProduction: process.env.REACT_APP_ENV === 'production' || !process.env.REACT_APP_ENV,
};

// Validate configuration and log for debugging
if (!config.apiUrl) {
  console.warn('⚠️ REACT_APP_API_URL is not set. Using default production URL.');
} else {
  // Log the API URL being used (helpful for debugging)
  console.log(`🔧 API Configuration: ${config.apiUrl}`);
  console.log(`🔧 Environment: ${config.environment}`);
}

// Ensure API URL uses HTTPS (not HTTP) for production/staging
if (config.apiUrl.startsWith('http://') && !config.apiUrl.includes('localhost')) {
  console.warn('⚠️ API URL is using HTTP instead of HTTPS. This may cause security issues.');
}

// Warn if using localhost when environment is staging/production
if (config.apiUrl.includes('localhost') && (config.isStaging || config.isProduction)) {
  console.error('❌ ERROR: Using localhost API URL in staging/production environment!');
  console.error('❌ Current API URL:', config.apiUrl);
  console.error('❌ Please update your .env.local or .env.development file to use the staging API:');
  console.error('❌ REACT_APP_API_URL=https://restaurant-reservations-api-staging.onrender.com/api/v1');
}

export default config;
